import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, UserPlus, X } from "lucide-react";
import { useGetIdentity, useNotify } from "ra-core";
import { useState } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";
import { useRelativeDate } from "../../atomic-crm/misc/RelativeDate";
import { EmptyState } from "../data-display/EmptyState";
import { GravunCard } from "../data-display/SectionCard";
import { StatusBadge } from "../data-display/StatusBadge";
import { ListPageHeader } from "../list/ListPageHeader";
import { useWorkspaces } from "../workspace/useWorkspaces";

interface MemberRow {
  id: string;
  user_id: string;
  created_at: string;
  role_id: string;
  roles: { id: string; key: string; name: string } | null;
}

interface ProfileRow {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: { src?: string } | null;
}

interface SystemRole {
  id: string;
  key: string;
  name: string;
}

interface InvitationRow {
  id: string;
  email: string;
  created_at: string;
  expires_at: string;
  roles: { name: string } | null;
}

const RelativeCell = ({ date }: { date: string }) => (
  <>{useRelativeDate(date)}</>
);

/**
 * Team management: real workspace members with roles, plus the
 * workspace_invitations flow, invited emails auto-join this workspace with
 * the assigned role when they sign up (handled by handle_new_user).
 */
export const TeamPage = () => {
  const { activeWorkspace } = useWorkspaces();
  const { data: identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const workspaceId = activeWorkspace?.workspaceId;

  const canManage =
    activeWorkspace != null &&
    ["workspace_owner", "administrator"].includes(activeWorkspace.roleKey);

  const { data: members = [] } = useQuery({
    queryKey: ["gravun", "team", "members", workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const supabase = getSupabaseClient();
      const [membersResult, profilesResult] = await Promise.all([
        supabase
          .from("workspace_members")
          .select("id, user_id, created_at, role_id, roles(id, key, name)")
          .eq("workspace_id", workspaceId!)
          .order("created_at"),
        supabase
          .from("sales")
          .select("user_id, first_name, last_name, email, avatar"),
      ]);
      if (membersResult.error) throw membersResult.error;
      if (profilesResult.error) throw profilesResult.error;
      const profiles = new Map(
        (profilesResult.data as ProfileRow[]).map((p) => [p.user_id, p]),
      );
      return (membersResult.data as unknown as MemberRow[]).map((member) => ({
        ...member,
        profile: profiles.get(member.user_id) ?? null,
      }));
    },
  });

  const { data: systemRoles = [] } = useQuery({
    queryKey: ["gravun", "system-roles"],
    staleTime: Infinity,
    queryFn: async (): Promise<SystemRole[]> => {
      const { data, error } = await getSupabaseClient()
        .from("roles")
        .select("id, key, name")
        .is("workspace_id", null)
        .order("name");
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: invitations = [] } = useQuery({
    queryKey: ["gravun", "team", "invitations", workspaceId],
    enabled: !!workspaceId && canManage,
    queryFn: async (): Promise<InvitationRow[]> => {
      const { data, error } = await getSupabaseClient()
        .from("workspace_invitations")
        .select("id, email, created_at, expires_at, roles(name)")
        .eq("workspace_id", workspaceId!)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as unknown as InvitationRow[];
    },
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["gravun", "team"] });

  const changeRole = useMutation({
    mutationFn: async ({
      memberId,
      roleId,
    }: {
      memberId: string;
      roleId: string;
    }) => {
      const { error } = await getSupabaseClient()
        .from("workspace_members")
        .update({ role_id: roleId })
        .eq("id", memberId);
      if (error) throw error;
    },
    onSuccess: () => {
      notify("Role updated", { type: "success" });
      invalidate();
    },
    onError: () => notify("Could not update the role", { type: "error" }),
  });

  const revokeInvitation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await getSupabaseClient()
        .from("workspace_invitations")
        .update({ status: "revoked" })
        .eq("id", invitationId);
      if (error) throw error;
    },
    onSuccess: () => {
      notify("Invitation revoked", { type: "info" });
      invalidate();
    },
    onError: () => notify("Could not revoke the invitation", { type: "error" }),
  });

  if (!activeWorkspace) return null;

  return (
    <div className="mx-auto max-w-4xl">
      <ListPageHeader
        title="Team"
        subtitle={`People with access to ${activeWorkspace.workspaceName}`}
        actions={
          canManage ? (
            <InviteDialog
              workspaceId={activeWorkspace.workspaceId}
              inviterId={String(identity?.id ?? "")}
              systemRoles={systemRoles}
              onInvited={invalidate}
            />
          ) : undefined
        }
      />

      <GravunCard className="overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2 font-medium">Member</th>
              <th className="px-3 py-2 font-medium">Role</th>
              <th className="px-4 py-2 text-right font-medium">Joined</th>
            </tr>
          </thead>
          <tbody>
            {members.map((member) => (
              <tr
                key={member.id}
                className="border-b border-border-subtle last:border-0"
              >
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <Avatar className="size-7">
                      <AvatarImage src={member.profile?.avatar?.src} alt="" />
                      <AvatarFallback className="bg-primary/10 text-[11px] font-semibold text-primary">
                        {(member.profile?.first_name?.[0] ?? "") +
                          (member.profile?.last_name?.[0] ?? "")}
                      </AvatarFallback>
                    </Avatar>
                    <span className="leading-tight">
                      <span className="block text-[13px] font-medium text-foreground">
                        {member.profile
                          ? `${member.profile.first_name} ${member.profile.last_name}`
                          : "Pending profile"}
                      </span>
                      <span className="block text-[11.5px] text-muted-foreground">
                        {member.profile?.email}
                      </span>
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5">
                  {canManage && member.user_id !== identity?.id ? (
                    <Select
                      value={member.role_id}
                      onValueChange={(roleId) =>
                        changeRole.mutate({ memberId: member.id, roleId })
                      }
                    >
                      <SelectTrigger className="h-8 w-44 rounded-md text-[13px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {systemRoles.map((role) => (
                          <SelectItem key={role.id} value={role.id}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <StatusBadge
                      label={member.roles?.name ?? "Member"}
                      tone="neutral"
                    />
                  )}
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-[12.5px] text-muted-foreground">
                  <RelativeCell date={member.created_at} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </GravunCard>

      {canManage ? (
        <div className="mt-6">
          <h2 className="mb-2 text-[14px] font-semibold tracking-tight text-foreground">
            Pending invitations
          </h2>
          <GravunCard className="overflow-hidden">
            {invitations.length === 0 ? (
              <EmptyState
                icon={Mail}
                title="No pending invitations"
                description="Invite a teammate and they will join this workspace when they sign up."
              />
            ) : (
              <table className="w-full">
                <tbody>
                  {invitations.map((invitation) => (
                    <tr
                      key={invitation.id}
                      className="border-b border-border-subtle last:border-0"
                    >
                      <td className="px-4 py-2.5 text-[13px] text-foreground">
                        {invitation.email}
                      </td>
                      <td className="px-3 py-2.5">
                        <StatusBadge
                          label={invitation.roles?.name ?? "Member"}
                          tone="info"
                        />
                      </td>
                      <td className="px-3 py-2.5 text-[12.5px] text-muted-foreground">
                        Invited <RelativeCell date={invitation.created_at} />
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-7 text-muted-foreground hover:text-destructive"
                          aria-label={`Revoke invitation for ${invitation.email}`}
                          onClick={() => revokeInvitation.mutate(invitation.id)}
                        >
                          <X className="size-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </GravunCard>
        </div>
      ) : null}
    </div>
  );
};

const InviteDialog = ({
  workspaceId,
  inviterId,
  systemRoles,
  onInvited,
}: {
  workspaceId: string;
  inviterId: string;
  systemRoles: SystemRole[];
  onInvited: () => void;
}) => {
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [roleId, setRoleId] = useState("");

  const invite = useMutation({
    mutationFn: async () => {
      const trimmed = email.trim().toLowerCase();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
        throw new Error("Enter a valid email address");
      }
      if (!roleId) throw new Error("Choose a role");
      const {
        data: { user },
      } = await getSupabaseClient().auth.getUser();
      const { error } = await getSupabaseClient()
        .from("workspace_invitations")
        .insert({
          workspace_id: workspaceId,
          email: trimmed,
          role_id: roleId,
          invited_by: user?.id ?? inviterId,
        });
      if (error) throw error;
      return trimmed;
    },
    onSuccess: (invitedEmail) => {
      notify(
        `Invitation created, ${invitedEmail} will join this workspace when they sign up with that email.`,
        { type: "success", autoHideDuration: 6000 },
      );
      setOpen(false);
      setEmail("");
      setRoleId("");
      onInvited();
    },
    onError: (error: Error) =>
      notify(error.message || "Could not create the invitation", {
        type: "error",
      }),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="h-8 gap-1.5 rounded-md px-2.5 text-[13px] font-medium shadow-none"
        >
          <UserPlus className="size-4" />
          Invite member
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite a team member</DialogTitle>
          <DialogDescription>
            They join this workspace with the selected role as soon as they sign
            up using this email address.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 py-1">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="teammate@company.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="invite-role">Role</Label>
            <Select value={roleId} onValueChange={setRoleId}>
              <SelectTrigger id="invite-role" className="rounded-md">
                <SelectValue placeholder="Choose a role" />
              </SelectTrigger>
              <SelectContent>
                {systemRoles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => invite.mutate()} disabled={invite.isPending}>
            {invite.isPending ? "Inviting..." : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
